import {ChangeEvent, useCallback, useEffect, useState, MouseEvent} from 'react';

import {observer} from 'mobx-react-lite';
import {prettyPrintJson} from 'pretty-print-json';
import {useHistory, useLocation} from 'react-router-dom';

import {
    Box,
    Button,
    FormControl,
    Grid,
    InputLabel,
    Link,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import BorderColorOutlinedIcon from '@material-ui/icons/BorderColorOutlined';
import {SelectInputProps} from '@material-ui/core/Select/SelectInput';

import {useDebounce} from 'use-debounce';

import CustomerPortalAPI from '../../APIs/CustomerPortalAPI';
import RootStore from '../../Core/mobx/RootStore';
import CustomerPortalStore from '../Customer/CustomerPortalStore/CustomerPortalStore';
import {DocumentType, underwritingRequirementType, UnderwritingRequirementUpdate} from '../types';
import allStatus from './AllStatus';
import RequirementsList, {RequirementType} from './RequirementTypes';

interface UnderwritingPanelProps {
    customerPortalStore: CustomerPortalStore;
}

const useStyles = makeStyles({
    wrapper: {},
    requirementLabel: {
        fontFamily: 'Roboto',
        fontStyle: 'normal',
        fontWeight: 700,
        fontSize: '1rem',
        lineHeight: '23px',
        color: '#000000',
    },
    requirementAddButton: {
        fontFamily: 'Roboto',
        fontStyle: 'normal',
        fontWeight: 600,
        fontSize: '0.875rem',
        lineHeight: '16px',
        textAlign: 'center',
        textTransform: 'uppercase',
        color: '#3B58B8',
    },
    requirements: {
        marginTop: '30px',
        overflowY: 'auto',
        height: 'calc(100vh - 430px)',
    },
    requirementItem: {
        display: 'flex',
        padding: '20px',
        borderRadius: '4px',
        marginBottom: '16px',
    },

    formWrapper: {
        padding: '32px',
        boxShadow: '0px 0px 6px rgba(0, 0, 0, 0.1)',
        borderRadius: '8px',
    },
});

const useFormStyles = makeStyles({
    container: {
        width: '100%',
        marginTop: '30px',
    },
    formInputLabel: {
        fontStyle: 'italic',
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '19px',
        color: '#757575',
        top: '-10px',
    },
    formInputLabelShrink: {
        transform: 'translate(0, 1.5px) scale(1)',
    },
    actionButtons: {
        display: 'flex',
        '& > button': {
            margin: '6px',
        },
    },
});

function UserIcon() {
    return (
        <svg width={18} height={18} viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M9.19775 12.1C12.217 12.1 14.6645 9.78307 14.6645 6.925C14.6645 4.06693 12.217 1.75 9.19775 1.75C6.17852 1.75 3.73096 4.06693 3.73096 6.925C3.73096 9.78307 6.17852 12.1 9.19775 12.1ZM9.19775 12.1C1.90869 12.1 1.90869 15.1892 1.90869 19M9.19775 12.1C16.4868 12.25 16.4868 15.1892 16.4868 19"
                stroke="black"
                strokeWidth={2}
                strokeLinecap="round"
            />
        </svg>
    );
}

type Props = {
    requirement: underwritingRequirementType | undefined;
};

function RequirementResponse({requirement}: Props) {
    const [rawJsonHtml, setRawJsonHtml] = useState('');

    useEffect(() => {
        if (requirement?.data_json && requirement?.data_json !== '{}') {
            setRawJsonHtml(prettyPrintJson.toHtml(JSON.parse(requirement.data_json)));
        } else {
            setRawJsonHtml('');
        }
    }, [requirement]);

    if (requirement?.status === 'outstanding') {
        return null;
    }

    return (
        <Box
            display="flex"
            flexDirection="column"
            borderColor="primary.main"
            border={1}
            width="100%"
            padding={2}
            borderRadius="borderRadius"
        >
            <Box fontWeight={700} fontSize="16px">
                Response
            </Box>
            {requirement?.data && requirement?.data !== '' ? (
                <Box paddingY={1}>
                    <Typography variant="body2">{requirement?.data}</Typography>
                </Box>
            ) : (
                <>
                    {requirement?.documents?.length !== 0 && (
                        <>
                            <Box marginY={1}>
                                <Box fontWeight={600} fontSize="14px">
                                    Documents
                                </Box>
                                <Box display="flex" flexDirection="column" marginY={1}>
                                    {requirement?.documents?.map((doc: DocumentType) => (
                                        <Link href={doc.downloadUrl} target="_blank">
                                            {doc.name}
                                        </Link>
                                    ))}
                                </Box>
                            </Box>
                        </>
                    )}
                    {rawJsonHtml && (
                        <Box marginY={1}>
                            <Box fontWeight={600} fontSize="14px">
                                JSON
                            </Box>
                            <pre
                                style={{overflow: 'scroll', whiteSpace: 'pre-wrap', wordBreak: 'break-all'}}
                                dangerouslySetInnerHTML={{__html: rawJsonHtml}}
                            />
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
}

interface RequirementFormProps {
    defaultValue: underwritingRequirementType | undefined;
    onChange: (
        e: ChangeEvent<
            | HTMLSelectElement
            | HTMLInputElement
            | {
                  name?: string | undefined;
                  value: unknown;
              }
        >,
    ) => void;
    onFormSubmit: (formData: unknown) => void;
    onFormCancel: () => void;
    isNewRequirement: boolean;
}

function RequirementForm({
    defaultValue,
    onChange: onChangeProp,
    onFormSubmit: onFormSubmitProp,
    onFormCancel: onFormCancelProp,
    isNewRequirement,
}: RequirementFormProps) {
    const classes = useFormStyles();

    const [dirty, setDirty] = useState(isNewRequirement);
    const [saving, setSaving] = useState(false);
    const [update, setUpdate] = useState<boolean>(false);
    const [formData, setFormData] = useState<unknown>();
    const [formDataDebounced] = useDebounce(formData, 300, {
        equalityFn() {
            return false;
        },
    });

    const refresh = () => {
        setUpdate(true);
        const timeout = setTimeout(() => {
            setUpdate(false);
            clearTimeout(timeout);
        }, 10);
    };

    useEffect(() => {
        setDirty(isNewRequirement);
        refresh();
        setFormData(defaultValue);
    }, [defaultValue, isNewRequirement]);

    type OnChangeParameters = Parameters<NonNullable<SelectInputProps['onChange']>>;
    type OnChangeEvent = OnChangeParameters[0];
    const onChange = (e: OnChangeEvent) => {
        onChangeProp(e);
        if (!dirty) {
            setDirty(true);
        }

        if (e.target.name && e.target.name.length > 0) {
            setFormData((oldFormData: any) => ({
                ...oldFormData,
                [e.target.name || 'any']: e.target.value,
            }));
        }
    };

    useEffect(() => {
        const promise = async () => {
            try {
                if (formDataDebounced) {
                    setSaving(true);
                    await onFormSubmitProp(formDataDebounced);
                    setDirty(false);
                }
            } catch (error) {
                console.error(error);
            } finally {
                setSaving(false);
            }
        };
        promise();
    }, [formDataDebounced]);

    const onFormCancel = () => {
        setDirty(false);
        refresh();
        onFormCancelProp();
    };

    const isUpdate = !!defaultValue && !isNewRequirement;

    if (update) {
        return null;
    }

    return (
        <form
            className={classes.container}
            onSubmit={() => onFormSubmitProp(formData)}
            autoComplete="new-password"
            noValidate
            onReset={onFormCancel}
        >
            <input type="hidden" name="id" value={defaultValue?.id} />
            <Grid container xs={12} spacing={2} alignContent="stretch">
                <Grid container item spacing={2} alignItems="flex-start">
                    <Grid item xs={4}>
                        <FormControl fullWidth>
                            <InputLabel
                                shrink
                                required
                                classes={{root: classes.formInputLabel, shrink: classes.formInputLabelShrink}}
                            >
                                Requirement
                            </InputLabel>
                            <Select
                                name="type"
                                defaultValue={defaultValue?.type}
                                onChange={onChange}
                                disabled={isUpdate}
                            >
                                {RequirementsList.map((requirementOption) => (
                                    <MenuItem value={requirementOption.type}>{requirementOption.title}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={4}>
                        <FormControl fullWidth>
                            <InputLabel
                                shrink
                                required
                                classes={{root: classes.formInputLabel, shrink: classes.formInputLabelShrink}}
                            >
                                Status
                            </InputLabel>
                            <Select name="status" defaultValue={defaultValue?.status} onChange={onChange}>
                                <MenuItem value="outstanding">Outstanding</MenuItem>
                                <MenuItem value="received">Received</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={4}>
                        <FormControl fullWidth>
                            <InputLabel
                                shrink
                                required
                                classes={{root: classes.formInputLabel, shrink: classes.formInputLabelShrink}}
                            >
                                Private Information
                            </InputLabel>
                            <Select
                                name="privateInfo"
                                defaultValue={defaultValue?.private ? 'yes' : 'no'}
                                onChange={onChange}
                                disabled={isUpdate}
                            >
                                <MenuItem value="yes">Yes</MenuItem>
                                <MenuItem value="no">No</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={4}>
                        <InputLabel className={classes.formInputLabel}>
                            Title of Requirement <span style={{color: 'red'}}>*</span>
                        </InputLabel>
                        <FormControl fullWidth>
                            <TextField
                                multiline
                                rows={4}
                                rowsMax={10}
                                hiddenLabel
                                name="title"
                                variant="outlined"
                                defaultValue={defaultValue?.title}
                                onChange={onChange}
                                disabled={isUpdate}
                                inputProps={{
                                    maxLength: 125,
                                }}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={4}>
                        <InputLabel className={classes.formInputLabel}>CM Notes</InputLabel>
                        <FormControl fullWidth>
                            <TextField
                                multiline
                                rows={4}
                                rowsMax={10}
                                name="internal_note"
                                variant="outlined"
                                defaultValue={defaultValue?.internal_note}
                                onChange={onChange}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={4}>
                        <InputLabel className={classes.formInputLabel}>Additional Instructions</InputLabel>
                        <FormControl fullWidth>
                            <TextField
                                multiline
                                rows={4}
                                rowsMax={10}
                                name="additional_instructions"
                                variant="outlined"
                                defaultValue={defaultValue?.additional_instructions}
                                onChange={onChange}
                                disabled={isUpdate}
                            />
                        </FormControl>
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <Box marginTop={5} width="230px" className={classes.actionButtons}>
                        <Button type="submit" color="primary" variant="contained" fullWidth disabled={!dirty || saving}>
                            {saving ? 'Saving...' : 'Save'}
                        </Button>
                        <Button type="reset" color="primary" variant="outlined" fullWidth disabled={!dirty || saving}>
                            Cancel
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </form>
    );
}

const UnderwritingPanel = observer(({customerPortalStore}: UnderwritingPanelProps) => {
    const classes = useStyles();

    const {selectedCustomer} = customerPortalStore;

    const [selectedRequirement, setSelectedRequirement] = useState<underwritingRequirementType | undefined>(undefined);

    const [requirements, setRequirements] = useState([] as underwritingRequirementType[]);

    const [firstRender, setFirstRender] = useState(true);
    const [isNewRequirement, setIsNewRequirement] = useState(false);
    const {search} = useLocation();
    const history = useHistory();
    const location = useLocation();

    const isUpdate = () => selectedRequirement && selectedRequirement.id && selectedRequirement.id.length > 0;

    const loadCustomerRequirements = async () => {
        if (selectedCustomer?.id) {
            try {
                const requirementsResponse = await CustomerPortalAPI.GetCustomerRequirements(selectedCustomer?.id);
                setRequirements([...(requirementsResponse?.requirements || [])]);
                if (!(selectedRequirement && selectedCustomer.id)) {
                    setSelectedRequirement(requirementsResponse?.requirements[0]);
                }
                RootStore.uiStore.showSnackbar(
                    `Successfully ${firstRender ? 'Loaded' : 'Updated'} Requirements`,
                    'success',
                );
                setFirstRender(false);
            } catch (error: any) {
                console.log(error);
                RootStore.uiStore.showSnackbar('Something went wrong, Check Logs', 'error');
            }
        }
    };

    const sendEnvelope = async (envelopeId: string, lockToken?: string) => {
        try {
            const sent = await CustomerPortalAPI.SendDocusignEnvelope(envelopeId, lockToken);
            if (sent) {
                return;
            }
        } catch (error: any) {
            console.log(error);
            RootStore.uiStore.showSnackbar('Something went wrong, Check Logs', 'error');
        }
    };

    useEffect(() => () => {
        RootStore.agentPortalStore.parameterStore.unmarkFormDirty('underwriting');
    });

    useEffect(() => {
        const searchParams = new URLSearchParams(search);
        const envelopeId = searchParams.get('envelopeId');
        const lockToken = searchParams.get('lockToken') ?? undefined;
        if (envelopeId) {
            searchParams.delete('envelopeId');
            searchParams.delete('lockToken');
            searchParams.delete('event');
            history.replace({pathname: location.pathname, search: searchParams.toString()});
            sendEnvelope(envelopeId, lockToken);
        }
    }, []);

    useEffect(() => {
        loadCustomerRequirements();
    }, [selectedCustomer]);

    type FormOnChangeParameters = Parameters<NonNullable<RequirementFormProps['onChange']>>;
    type FormOnChangeEvent = FormOnChangeParameters[0];
    const onFormStateChange = (e: FormOnChangeEvent) => {
        if (e.target.name === 'type') {
            const template = RequirementsList.find(
                (requirement) => requirement.type === e.target.value,
            ) as RequirementType;
            setSelectedRequirement({
                status: 'outstanding',
                ...template,
                updatedAt: new Date().toString(),
            } as underwritingRequirementType);
        }
        RootStore.agentPortalStore.parameterStore.markFormDirty('underwriting');
    };

    const onFormSubmitCallback = useCallback(
        async (formData) => {
            if (!selectedCustomer?.id) {
                RootStore.uiStore.showSnackbar('Customer is not associated, Please select the customer', 'error');
                return;
            }

            const updatedRequirement: underwritingRequirementType | UnderwritingRequirementUpdate = {
                ...(!isUpdate()
                    ? {
                          private: formData.privateInfo === 'yes',
                          type: formData.type.toString(),
                          status: formData.status.toString(),
                          title: formData.title.toString(),
                          internal_note: formData.internal_note?.toString() ?? '',
                          additional_instructions: formData.additional_instructions?.toString() ?? '',
                          dsReturnUrl: `${window.location.href}`,
                      }
                    : {
                          ...selectedRequirement,
                          id: formData.id.toString(),
                          status: formData.status.toString(),
                          internal_note: formData.internal_note?.toString() ?? '',
                      }),
            };

            const updateResponse = await CustomerPortalAPI.UpdateUnderwritingRequirements({
                customerId: selectedCustomer?.id,
                newRequirements: [updatedRequirement],
            });

            RootStore.agentPortalStore.parameterStore.unmarkFormDirty('underwriting');

            // for confirm signature (docusign) requirements send the user to docusign to add them.
            if (updateResponse?.redirectUrl) {
                window.location.href = updateResponse.redirectUrl;
            }
            loadCustomerRequirements();
            setIsNewRequirement(false);
        },
        [selectedRequirement],
    );

    const onFormCancelCallback = useCallback(() => {
        if (!isUpdate()) {
            setSelectedRequirement(requirements[0]);
        }
        RootStore.agentPortalStore.parameterStore.unmarkFormDirty('underwriting');
    }, [isUpdate, requirements]);

    const onRequirementSelectCallback = useCallback(
        (e: MouseEvent<HTMLDivElement>) => {
            const requirement = requirements.find((r) => r?.id === e?.currentTarget.dataset.id);
            setSelectedRequirement(requirement);
        },
        [requirements],
    );

    const onAddRequirementCallback = useCallback(() => {
        setSelectedRequirement(undefined);
        setIsNewRequirement(true);
        RootStore.agentPortalStore.parameterStore.markFormDirty('underwriting'); // marks unsaved data for underwriting form
    }, [requirements]);

    return (
        <Grid container spacing={4} classes={{root: classes.wrapper}}>
            <Grid container item xs={7} md={8} lg={9} spacing={3} alignContent="flex-start">
                <Grid item xs={12}>
                    <Box fontWeight={700} fontSize="18px" lineHeight="30px">
                        Requirements Viewer
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Paper classes={{root: classes.formWrapper}}>
                        <Box display="flex">
                            <UserIcon />
                            <Box fontWeight={700} fontSize="16px" marginLeft={1}>
                                Underwriting Requirement
                            </Box>
                        </Box>
                        <Grid item container xs={12}>
                            <RequirementForm
                                defaultValue={selectedRequirement}
                                onChange={onFormStateChange}
                                onFormSubmit={onFormSubmitCallback}
                                onFormCancel={onFormCancelCallback}
                                isNewRequirement={isNewRequirement}
                            />
                        </Grid>
                        <Grid item container xs={12}>
                            <RequirementResponse requirement={selectedRequirement} />
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>

            <Grid
                container
                item
                xs={5}
                md={4}
                lg={3}
                style={{
                    backgroundColor: '#F8F8F8',
                    padding: '24px',
                }}
            >
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="space-between" alignContent="center" alignItems="center">
                        <Typography classes={{root: classes.requirementLabel}}>Requirements</Typography>
                        <Button type="button" onClick={onAddRequirementCallback}>
                            <Typography classes={{root: classes.requirementAddButton}}>+ add requirement</Typography>
                        </Button>
                    </Box>

                    <Box className={classes.requirements}>
                        {requirements.map((requirement) => (
                            <Box
                                bgcolor={selectedRequirement?.id === requirement.id ? '#E1E5F1' : 'transparent'}
                                className={classes.requirementItem}
                                role="button"
                                data-id={requirement.id}
                                onClick={onRequirementSelectCallback}
                            >
                                <BorderColorOutlinedIcon color="primary" style={{paddingTop: '0.285em'}} />
                                <Box display="flex" flexDirection="column" paddingLeft={2.5}>
                                    <Typography color="primary" variant="subtitle1">
                                        {requirement.title}
                                    </Typography>
                                    <Typography variant="caption" color="primary" display="block" gutterBottom>
                                        {RequirementsList.filter((req) => req.type === requirement.type)[0]?.title}
                                    </Typography>
                                    <Typography color="primary" variant="subtitle2">
                                        {allStatus.find((status) => status.name === requirement.status)?.description}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Grid>
            </Grid>
        </Grid>
    );
});

export default UnderwritingPanel;
